import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useSubscription } from '@/hooks/useSubscription'
import { getTierFeatures } from '@/lib/stripe'

export interface Module {
  id: string
  name: string
  description: string | null
  category: 'core' | 'professional' | 'enterprise' | 'addon'
  monthly_price_gbp: number
  annual_price_gbp: number
  requires_modules: string[]
  conflicts_with: string[]
  is_active: boolean
  is_beta: boolean
  icon: string | null
  color: string | null
  sort_order: number
}

export interface OrganizationModule {
  id: string
  organization_id: string
  module_id: string
  enabled: boolean
  enabled_at: string
  enabled_by: string | null
  is_trial: boolean
  trial_ends_at: string | null
  expires_at: string | null
  usage_limit: number | null
  usage_count: number
  module?: Module
}

/**
 * Hook to fetch all available modules in the system
 */
export function useAvailableModules() {
  return useQuery({
    queryKey: ['modules'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('modules')
        .select('*')
        .eq('is_active', true)
        .order('sort_order')

      if (error) throw error
      return data as Module[]
    },
  })
}

/**
 * Hook to fetch the current user's organization details
 * Includes dev_mode_all_modules flag for development bypass
 */
export function useOrganization() {
  return useQuery({
    queryKey: ['current-organization'],
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single()

      if (profileError) throw profileError
      if (!profile?.organization_id) throw new Error('No organization found')

      const { data: org, error } = await supabase
        .from('organizations')
        .select('id, name, dev_mode_all_modules, subscription_tier, subscription_status')
        .eq('id', profile.organization_id)
        .single()

      if (error) throw error
      return org
    },
  })
}

/**
 * Hook to fetch enabled modules for the current user's organization
 */
export function useOrganizationModules() {
  return useQuery({
    queryKey: ['organization-modules'],
    queryFn: async () => {
      // Get current user's organization
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single()

      if (profileError) throw profileError
      if (!profile?.organization_id) throw new Error('No organization found')

      // Fetch enabled modules with module details
      const { data, error } = await supabase
        .from('organization_modules')
        .select(
          `
          *,
          module:modules(*)
        `
        )
        .eq('organization_id', profile.organization_id)
        .eq('enabled', true)

      if (error) throw error

      return data as OrganizationModule[]
    },
  })
}

/**
 * Hook to check if a specific module is enabled for the current organization.
 * Checks in order:
 * 1. Dev mode bypass
 * 2. Subscription tier (trial users get Professional features, paid users get their tier's features)
 * 3. organization_modules table (for add-ons or explicit grants)
 */
export function useHasModule(moduleId: string) {
  const { data: org, isLoading: orgLoading } = useOrganization()
  const { data: modules, isLoading: modulesLoading } = useOrganizationModules()
  const subscription = useSubscription()

  const isLoading = orgLoading || modulesLoading || subscription.loading

  // Don't make access decisions until data is loaded
  if (isLoading) {
    return { hasModule: false, isLoading: true, isDevMode: false }
  }

  // If dev mode is enabled, grant access to all modules
  if (org?.dev_mode_all_modules) {
    return {
      hasModule: true,
      isLoading: false,
      isDevMode: true,
    }
  }

  // Check if subscription tier includes this module
  if (subscription.isActive) {
    const tierModules = getTierFeatures(subscription.planTier)
    if (tierModules.includes(moduleId)) {
      return {
        hasModule: true,
        isLoading: false,
        isDevMode: false,
      }
    }
  }

  // Fallback: check organization_modules table (for add-ons)
  const hasModule =
    modules?.some((om) => {
      if (om.module_id !== moduleId || !om.enabled) return false

      // Check expiry
      if (om.expires_at && new Date(om.expires_at) < new Date()) return false

      // Check trial
      if (om.is_trial && om.trial_ends_at && new Date(om.trial_ends_at) < new Date()) {
        return false
      }

      return true
    }) ?? false

  return {
    hasModule,
    isLoading,
    isDevMode: false,
  }
}

/**
 * Hook to get module details by ID
 */
export function useModule(moduleId: string) {
  const { data: modules } = useAvailableModules()
  return modules?.find((m) => m.id === moduleId)
}

/**
 * Helper to check if module is in trial period
 */
export function isModuleTrial(module: OrganizationModule): boolean {
  if (!module.is_trial) return false
  if (!module.trial_ends_at) return false
  return new Date(module.trial_ends_at) > new Date()
}

/**
 * Helper to get days remaining in trial
 */
export function getTrialDaysRemaining(module: OrganizationModule): number | null {
  if (!isModuleTrial(module)) return null
  if (!module.trial_ends_at) return null

  const now = new Date()
  const trialEnd = new Date(module.trial_ends_at)
  const daysRemaining = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

  return Math.max(0, daysRemaining)
}

/**
 * Helper to check if module has expired
 */
export function isModuleExpired(module: OrganizationModule): boolean {
  if (module.expires_at && new Date(module.expires_at) < new Date()) return true
  if (module.is_trial && module.trial_ends_at && new Date(module.trial_ends_at) < new Date()) {
    return true
  }
  return false
}
